'use client'

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, BadgeCheck, Clock, ShieldCheck, Sparkles, Wrench, X } from 'lucide-react'

const photos = [
  '/tank1.jpeg',
  '/tank5.jpeg',
  '/tank6.jpeg',
  '/tank7.jpeg',
  '/tank8.jpeg',
  '/tank9.jpeg',
  '/tank10.jpeg',
  '/tank11.jpeg',
]

const videos = ['/tank2.mp4', '/tank3.mp4', '/tank4.mp4']

const heroSlides = [
  { type: 'video', src: '/tank2.mp4', poster: '/tank1.jpeg' },
  { type: 'image', src: '/tank1.jpeg' },
  { type: 'video', src: '/tank3.mp4', poster: '/tank5.jpeg' },
  { type: 'image', src: '/tank6.jpeg' },
]

const features = [
  {
    title: 'MODEL-SPECIFIC FIT',
    text: 'Shaped around the motorcycle for secure mounting, clean alignment and a finish that looks integrated, not improvised.',
    icon: ShieldCheck,
  },
  {
    title: 'PURPOSEFUL PROTECTION',
    text: 'Helps shield high-contact areas from jacket abrasion, luggage rub, minor scuffs and everyday riding wear.',
    icon: BadgeCheck,
  },
  {
    title: 'TOURING-READY DESIGN',
    text: 'Built for daily commutes, weekend rides and long-distance touring, with practical grip points and durable construction.',
    icon: Wrench,
  },
  {
    title: 'SUPPORTED AFTER PURCHASE',
    text: 'Get compatibility guidance, installation assistance and responsive after-sales support throughout your ownership experience.',
    icon: Clock,
  },
]

const TankCover = () => {
  const navigate = useNavigate()
  const [activeSlide, setActiveSlide] = useState(0)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1))
    }, 4200)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const navbar = document.querySelector('nav')
    if (navbar) {
      if (isVideoPlaying) {
        navbar.style.transform = 'translateY(-100%)'
        navbar.style.transition = 'transform 0.3s ease'
      } else {
        navbar.style.transform = 'translateY(0)'
        navbar.style.transition = 'transform 0.3s ease'
      }
    }

    // Cleanup: reset navbar display when component unmounts
    return () => {
      if (navbar) {
        navbar.style.transform = 'translateY(0)'
      }
    }
  }, [isVideoPlaying])

  return (
    <div className="min-h-screen bg-black text-white">
      <div className={`transition-all duration-300 ${isVideoPlaying ? 'h-0' : 'h-16 md:h-20'}`} />

    {/* ================= DESKTOP HERO ================= */}
<section className="relative w-full overflow-hidden">
  <div className="relative h-[560px] md:h-[700px] w-full overflow-hidden">

    {/* Background Slides */}
    {heroSlides.map((slide, index) => (
      <motion.div
        key={`${slide.type}-${slide.src}`}
        className="absolute inset-0"
        animate={{ opacity: activeSlide === index ? 1 : 0 }}
        transition={{ duration: 0.8 }}
      >
        {slide.type === "video" ? (
          <video
            className="h-full w-full object-cover object-center scale-[1.03]"
            src={slide.src}
            poster={slide.poster}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={slide.src}
            alt="RRide tank cover"
            className="h-full w-full object-cover object-center scale-[1.03]"
          />
        )}
      </motion.div>
    ))}
{/* Light cinematic overlay */}
<div className="absolute inset-0 bg-black/5" />

{/* Bottom fade only */}
<div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
    {/* Content */}
    <div className="relative z-20 flex h-full items-end">
      <div className="w-full max-w-7xl px-6 pb-14 md:px-20 md:pb-24">

        {/* Badge */}
        <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 backdrop-blur-xl px-5 py-2">
          <div className="h-2 w-2 rounded-full bg-[#ffcc32]" />

          <span className="text-[11px] uppercase tracking-[3px] font-semibold text-[#ffcc32]">
            ONLY AT RRIDE
          </span>
        </div>

        {/* Heading */}
        <h1
          className="mt-6 uppercase leading-[0.88] text-white"
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "clamp(2.35rem,11vw,6rem)",
            textShadow: "0 6px 18px rgba(0,0,0,.35)",
          }}
        >
          RRide Tank Covers
        </h1>

        {/* Description */}
        <p className="mt-5 max-w-[620px] text-sm leading-6 text-white/85 sm:text-base md:mt-6 md:text-lg md:leading-8">
          Premium Tank Covers Exclusively Available at RRide.
          Designed for perfect fit, premium finish and maximum tank protection.
        </p>

        {/* Buttons */}
        <div className="mt-10 flex flex-wrap gap-4">

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: .98 }}
            onClick={() => navigate("/products?product_type=gear&is_outran=true")}
            className="group flex h-[60px] items-center gap-5 rounded-2xl bg-[#ffcc32] px-8 font-bold text-black shadow-[0_18px_45px_rgba(255,204,50,.28)]"
          >
            <span className="tracking-wide">
              Shop Now
            </span>

            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-[#ffcc32] transition-all duration-300 group-hover:translate-x-1">
              <ArrowRight size={18} />
            </div>
          </motion.button>

          <motion.a
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: .98 }}
            href="#rride-tank-cover-videos"
            className="flex h-[62px] items-center rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl px-8 font-semibold text-white transition-all duration-300 hover:bg-white/15 hover:border-[#ffcc32]/40"
          >
            Watch Video
          </motion.a>

        </div>

      </div>
    </div>

  

  </div>
</section>

      

      <section className="border-y border-white/10 bg-[#070907] px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#ffcc32]">About The Drop</p>
            <h2 className="mt-3 text-4xl font-black uppercase leading-none text-white md:text-6xl" style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: "0.05em" }}>
              BUILT TO BELONG ON THE BIKE.
            </h2>
          </div>
          <p className="text-sm leading-7 text-zinc-300 md:text-base md:leading-8">
            Designed for daily riding and long-distance touring, RRide tank covers bring together durable construction, rider-focused grip and model-specific fitment. They protect high-contact areas while adding a refined, purposeful finish to the motorcycle.
          </p>
        </div>
      </section>

      <section id="rride-tank-cover-videos" className="scroll-mt-28 px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-[#ffcc32]">Product Preview</p>
              <h2 className="mt-3 text-3xl font-black uppercase text-white md:text-5xl" style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: "0.05em" }}>
                Video first look
              </h2>
            </div>
            <Sparkles className="hidden h-8 w-8 text-[#ffcc32] md:block" />
          </div>

          <div className="overflow-hidden rounded-[18px] border border-white/10 bg-[#080808]">
            <video
              className="w-full h-auto max-h-[500px]"
              src={videos[0]}
              poster={photos[0] || '/tank1.jpeg'}
              controls
              playsInline
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
              onEnded={() => setIsVideoPlaying(false)}
            />
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs uppercase tracking-[0.35em] text-[#ffcc32]">Why It Matters</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-[14px] border border-white/10 bg-black p-5"
                >
                  <Icon className="mb-4 h-6 w-6 text-[#ffcc32]" />
                  <h3 className="text-lg font-black uppercase text-white">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-400">{feature.text}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.35em] text-[#ffcc32]">Gallery</p>
            <h2 className="mt-3 text-3xl font-black uppercase text-white md:text-5xl" style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: "0.05em" }}>
              RRide Tank Cover photos
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {photos.map((photo, index) => (
              <motion.button
                key={photo}
                type="button"
                onClick={() => setSelectedPhoto(photo)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.04 }}
                className="overflow-hidden rounded-[16px] border border-white/10 bg-white text-left"
              >
                <img src={photo} alt={`RRide tank cover preview ${index + 1}`} className="aspect-[4/5] h-full w-full object-cover transition duration-500 hover:scale-105" />
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {selectedPhoto && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedPhoto(null)}>
          <button
            type="button"
            onClick={() => setSelectedPhoto(null)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white"
            aria-label="Close photo viewer"
          >
            <X size={22} />
          </button>
          <img
            src={selectedPhoto}
            alt="RRide tank cover large preview"
            className="max-h-[88vh] max-w-[94vw] rounded-[18px] object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

export default TankCover
